import type {DataGridSettings} from '../types';

type LegacyColumnConfig = {
  name?: string;
  visible?: boolean;
  width?: number;
};

/** Settings stored per table before the datagrid owned its own state */
export type LegacyTableSettings = {
  columns?: LegacyColumnConfig[];
  sorting?: {field?: string; sort?: 'asc' | 'desc'};
  filterModel?: NonNullable<DataGridSettings['filter']>['filterModel'];
  paginationModel?: {page?: number; pageSize?: number};
};

type ColumnDimensions = NonNullable<
  NonNullable<DataGridSettings['columns']>['dimensions']
>;

const mapColumns = (
  columns: LegacyColumnConfig[] | undefined,
): DataGridSettings['columns'] => {
  const named = (Array.isArray(columns) ? columns : []).filter(
    (column): column is LegacyColumnConfig & {name: string} =>
      typeof column?.name === 'string',
  );

  if (named.length === 0) {
    return undefined;
  }

  const dimensions = named.reduce<ColumnDimensions>((acc, {name, width}) => {
    if (width !== undefined) {
      acc[name] = {width};
    }
    return acc;
  }, {});

  return {
    // Columns were hidden unless explicitly visible
    columnVisibilityModel: Object.fromEntries(
      named.map(({name, visible}) => [name, visible === true]),
    ),
    orderedFields: named.map(({name}) => name),
    ...(Object.keys(dimensions).length > 0 ? {dimensions} : {}),
  };
};

const mapTable = (
  settings: LegacyTableSettings,
): DataGridSettings | undefined => {
  const {sorting, filterModel, paginationModel} = settings;

  const converted: DataGridSettings = {
    columns: mapColumns(settings.columns),
    sorting:
      sorting?.field && sorting.sort
        ? {sortModel: [{field: sorting.field, sort: sorting.sort}]}
        : undefined,
    filter: filterModel?.items?.length ? {filterModel} : undefined,
    // The page is not converted, most tables started on the first page
    pagination:
      typeof paginationModel?.pageSize === 'number'
        ? {paginationModel: {page: 0, pageSize: paginationModel.pageSize}}
        : undefined,
  };

  return Object.values(converted).some(part => part !== undefined)
    ? converted
    : undefined;
};

/**
 * Converts settings stored per table to datagrid settings, so users keep
 * their column layout, sorting and filters.
 */
export const mapLegacyTableSettings = (
  tables: Record<string, LegacyTableSettings> | undefined,
): Record<string, DataGridSettings> => {
  return Object.entries(tables ?? {}).reduce<Record<string, DataGridSettings>>(
    (acc, [name, settings]) => {
      const converted = settings ? mapTable(settings) : undefined;
      if (converted) {
        acc[name] = converted;
      }
      return acc;
    },
    {},
  );
};
